import _ from 'lodash';
import { useLayoutEffect, useRef } from 'react';
import type {
  MagneticWallContentRect,
  MagneticWallDisplayItem,
} from '../MagneticWall';
import { MINIMIZED_COMMENT_HEIGHT } from './CommentThreadMinimized';
import useAppSelector from '../../app/redux/useAppSelector';
import useMagneticWallItemSize from '../useMagneticWallItemSize';
import type { DisplayId } from '../utils/toDisplayId';
import toDisplayId from '../utils/toDisplayId';
import { ANIMATED_COMMENT_PREVIEW_DURATION } from './CommentPreview';

export const POPOVER_BELOW_CONTENT_OFFSET = 8;
const AVOID_CONTENT_THRESHOLD_HEIGHT = 380;
export const THREAD_SPACING = 12;

export type Heights = Record<DisplayId, number | null>;
export type ContentRects = Record<string, MagneticWallContentRect>;
export type WiderLineRange = { top: number; bottom: number };

type IntermediatePosition = {
  displayId: DisplayId;
  contentHeight: number;
  height: number;
  top: number;
};

type ResultPosition = { top: number; isMinimized: boolean };

function hasItems<Item>(items: Item[]): items is [Item, ...Item[]] {
  return items.length !== 0;
}

const isBesideWiderLines = (
  widerLineRanges: WiderLineRange[],
  top: number,
  height = 0,
) => {
  return widerLineRanges.some(
    (range) => top + height >= range.top && top <= range.bottom,
  );
};

const getAvoidContentOverlapOffset = (contentHeight: number) => {
  return contentHeight < AVOID_CONTENT_THRESHOLD_HEIGHT
    ? contentHeight + POPOVER_BELOW_CONTENT_OFFSET
    : 0;
};

function adjustPositions({
  intermediatePositions,
  widerLineRanges,
  priorityIndex,
  highlightId,
  positions,
}: {
  intermediatePositions: [IntermediatePosition, ...IntermediatePosition[]];
  widerLineRanges: WiderLineRange[];
  priorityIndex: number;
  highlightId: DisplayId | null;
  positions: Record<string, ResultPosition>;
}): Record<string, ResultPosition> {
  const adjustedPositions = { ...positions };

  for (let i = priorityIndex - 1; i >= 0; i -= 1) {
    const current = intermediatePositions[i];
    const next = intermediatePositions[i + 1];
    const nextAdjustedPosition = next
      ? adjustedPositions[next.displayId]
      : null;

    if (current && nextAdjustedPosition) {
      const adjustedTopOffset =
        nextAdjustedPosition.top - current.height - THREAD_SPACING;

      const upperTopOffset = Math.min(current.top, adjustedTopOffset);
      const shouldAllowWiderLineOverlap = current.displayId === highlightId;
      const isMinimized = shouldAllowWiderLineOverlap
        ? isBesideWiderLines(widerLineRanges, upperTopOffset)
        : isBesideWiderLines(widerLineRanges, upperTopOffset, current.height);

      adjustedPositions[current.displayId] = {
        isMinimized,
        top:
          upperTopOffset === adjustedTopOffset && isMinimized
            ? upperTopOffset +
              Math.max(current.height - MINIMIZED_COMMENT_HEIGHT, 0)
            : upperTopOffset,
      };
    }
  }

  for (let i = priorityIndex + 1; i < intermediatePositions.length; i += 1) {
    const current = intermediatePositions[i];
    const prev = intermediatePositions[i - 1];

    const adjustedPrevPosition = prev
      ? adjustedPositions[prev.displayId]
      : null;

    if (current && prev && adjustedPrevPosition) {
      const adjustedTopOffset =
        adjustedPrevPosition.top +
        (adjustedPrevPosition.isMinimized
          ? MINIMIZED_COMMENT_HEIGHT
          : prev.height) +
        THREAD_SPACING;

      const lowerTopOffset = Math.max(current.top, adjustedTopOffset);
      const shouldAllowWiderLineOverlap = current.displayId === highlightId;

      const isMinimized = shouldAllowWiderLineOverlap
        ? isBesideWiderLines(widerLineRanges, lowerTopOffset)
        : isBesideWiderLines(widerLineRanges, lowerTopOffset, current.height);

      adjustedPositions[current.displayId] = {
        isMinimized,
        top: lowerTopOffset,
      };
    }
  }

  return adjustedPositions;
}

function extendWithContentRects(
  items: MagneticWallDisplayItem[],
  contentRects: ContentRects,
) {
  return _.compact(
    items.map((item) => {
      const contentRect = contentRects[item.data.id];
      if (!contentRect) return null;

      return {
        ...item,
        contentRect,
      };
    }),
  );
}

function sortByContentRectPostion(
  a: { contentRect: MagneticWallContentRect },
  b: { contentRect: MagneticWallContentRect },
) {
  return a.contentRect.top === b.contentRect.top
    ? a.contentRect.left - b.contentRect.left
    : a.contentRect.top - b.contentRect.top;
}

function toIntermediatePosition(
  item: MagneticWallDisplayItem & { contentRect: MagneticWallContentRect },
  heights: Heights,
): IntermediatePosition {
  const height = heights[item.displayId] ?? MINIMIZED_COMMENT_HEIGHT;
  const { top, bottom } = item.contentRect;

  return {
    displayId: item.displayId,
    contentHeight: bottom - top,
    height,
    top,
  };
}

export function getPositionsWithoutOverlaps({
  heights,
  magneticWallItems,
  shouldAvoidContentOverlap,
  widerLineRanges,
  contentRects,
  highlightId,
  priorityId,
  previousPositions,
}: {
  heights: Heights;
  magneticWallItems: MagneticWallDisplayItem[];
  shouldAvoidContentOverlap: boolean;
  widerLineRanges: WiderLineRange[];
  contentRects: ContentRects;
  highlightId: DisplayId | null;
  priorityId: DisplayId | null;
  previousPositions: Record<string, ResultPosition>;
}) {
  const intermediatePositions = extendWithContentRects(
    magneticWallItems,
    contentRects,
  )
    .sort(sortByContentRectPostion)
    .map((item) => toIntermediatePosition(item, heights));

  if (!hasItems(intermediatePositions)) {
    return {};
  }

  const priorityIndex = priorityId
    ? Math.max(
        0,
        intermediatePositions.findIndex(
          (item) => item.displayId === priorityId,
        ),
      )
    : 0;

  const priorityItem =
    intermediatePositions[priorityIndex] ?? intermediatePositions[0];

  const wasPreviouslyMinimized =
    previousPositions[priorityItem.displayId]?.isMinimized ?? false;

  const shouldAllowWiderLineOverlap =
    priorityId === priorityItem.displayId ||
    highlightId === priorityItem.displayId;

  const isMinimized =
    // when an item was previously minimized, we want to rather keep it minimized
    // than to allow it to overlap with wider lines in an unminimized state for
    // a better feeling of consistency
    !wasPreviouslyMinimized && shouldAllowWiderLineOverlap
      ? isBesideWiderLines(widerLineRanges, priorityItem.top)
      : isBesideWiderLines(
          widerLineRanges,
          priorityItem.top,
          priorityItem.height,
        );

  const top = shouldAvoidContentOverlap
    ? priorityItem.top +
      getAvoidContentOverlapOffset(priorityItem.contentHeight)
    : priorityItem.top;

  const positions = {
    [priorityItem.displayId]: {
      top,
      isMinimized,
    },
  };

  return adjustPositions({
    intermediatePositions,
    widerLineRanges,
    priorityIndex,
    highlightId,
    positions,
  });
}

const PRIORITY_HIGHLIGHT_DEBOUNCE_WAIT_TIME =
  ANIMATED_COMMENT_PREVIEW_DURATION * 1000;

function useDefferredId(value: DisplayId | null) {
  const valueRef = useRef<DisplayId | null>(null);

  useLayoutEffect(() => {
    if (value == null) {
      const timeout = setTimeout(() => {
        valueRef.current = null;
      }, PRIORITY_HIGHLIGHT_DEBOUNCE_WAIT_TIME);

      return () => {
        clearTimeout(timeout);
      };
    }

    valueRef.current = value;
    return undefined;
  }, [value]);

  return value ?? valueRef.current;
}

function usePositionsWithoutOverlaps({
  heights,
  magneticWallItems,
  widerLineRanges,
  contentRects,
}: {
  heights: Heights;
  magneticWallItems: MagneticWallDisplayItem[];
  widerLineRanges: WiderLineRange[];
  contentRects: ContentRects;
}) {
  const prevPositionsRef = useRef<
    ReturnType<typeof getPositionsWithoutOverlaps>
  >({});
  const magneticWallItemSize = useMagneticWallItemSize();
  const magneticWallFocus = useAppSelector(
    (state) => state.editor.magneticWallFocus,
  );

  const magneticWallHighlight = useAppSelector(
    (state) => state.editor.magneticWallHighlight,
  );

  const shouldAvoidContentOverlap =
    magneticWallItemSize === 'compact' && !!magneticWallFocus?.active;

  const priorityId = magneticWallFocus ? toDisplayId(magneticWallFocus) : null;
  const highlightId = magneticWallHighlight
    ? toDisplayId(magneticWallHighlight)
    : null;

  // we want to keep both priorityId and highlightId around for the amount of time
  // it takes for the comment preview to animate, so that users don't experience
  // any jumping behaviour when hovering over comments
  const deferredPriorityId = useDefferredId(priorityId);

  const deferredHighlightId = useDefferredId(highlightId);

  const positions = getPositionsWithoutOverlaps({
    heights,
    magneticWallItems,
    shouldAvoidContentOverlap,
    widerLineRanges,
    contentRects,
    priorityId: deferredPriorityId,
    highlightId: deferredHighlightId,
    previousPositions: prevPositionsRef.current,
  });

  useLayoutEffect(() => {
    prevPositionsRef.current = positions;
  }, [positions]);

  return positions;
}

export default usePositionsWithoutOverlaps;
